import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type IndicacaoItem,
  obterCliente,
  obterIndicacoesDoCliente,
  type VoucherCliente,
  listarVouchersDoCliente,
} from '../api/clientes';
import { listarOrdensDoCliente, type OrdemServico } from '../api/ordens';
import { DrawerAnonimizarCliente } from '../components/DrawerAnonimizarCliente';
import { DrawerCancelarOS } from '../components/DrawerCancelarOS';
import { DrawerEntrega } from '../components/DrawerEntrega';
import { CabecalhoTela } from '../components/Shell';
import { BlocoAviso, Botao, Chip, LinhasEsqueleto, PlacaCodigo } from '../components/ui';
import { useAuth } from '../lib/auth';
import { mensagemDeErro } from '../lib/erros';
import { ApiError } from '../lib/http';
import { formatarData } from './Clientes';

export function ClienteFichaPagina(): ReactNode {
  const { id = '' } = useParams();
  const { usuario } = useAuth();
  const cliente = useQuery({ queryKey: ['cliente', id], queryFn: () => obterCliente(id) });
  const [anonimizar, setAnonimizar] = useState(false);

  if (cliente.isLoading) {
    return (
      <div>
        <CabecalhoTela titulo="Cliente" />
        <LinhasEsqueleto linhas={4} />
      </div>
    );
  }

  if (cliente.isError) {
    const naoAchou = cliente.error instanceof ApiError && cliente.error.status === 404;
    return (
      <div>
        <CabecalhoTela titulo="Cliente" />
        <BlocoAviso
          tom="aviso"
          aoTentarDeNovo={naoAchou ? undefined : () => void cliente.refetch()}
        >
          {naoAchou ? 'Cliente não encontrado.' : 'Não foi possível carregar a ficha.'}
        </BlocoAviso>
        <Link to="/clientes" className="mt-3 inline-block text-corpo text-acento-texto underline">
          Voltar para a lista
        </Link>
      </div>
    );
  }

  const c = cliente.data!;
  const cr = c.crescimento;
  const primeiro = c.nome.trim().split(/\s+/)[0] ?? c.nome;

  const anonimizado = Boolean(c.anonimizadoEm);

  return (
    <div className="flex flex-col gap-6">
      <CabecalhoTela
        titulo={c.nome}
        acoes={
          usuario?.role === 'admin' && !anonimizado ? (
            <Botao variante="perigo" onClick={() => setAnonimizar(true)}>
              Anonimizar dados
            </Botao>
          ) : undefined
        }
      />

      {anonimizado && (
        <BlocoAviso tom="aviso">
          Dados pessoais deste cliente foram anonimizados em {formatarData(c.anonimizadoEm!)}. O
          cadastro não pode mais ser editado.
        </BlocoAviso>
      )}

      {!c.ativo && !anonimizado && (
        <BlocoAviso tom="aviso">
          Cadastro inativo. As indicações feitas por ele não convertem enquanto estiver inativo.
        </BlocoAviso>
      )}

      {anonimizar && (
        <DrawerAnonimizarCliente
          clienteId={id}
          clienteNome={c.nome}
          aberto
          aoFechar={() => setAnonimizar(false)}
        />
      )}

      {/* Cabeçalho */}
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-2 border border-linha bg-superficie p-4">
        <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1 text-corpo">
          {c.tipoParticipante === 'funcionario' && (
            <>
              <dt className="text-tinta-2">Tipo</dt>
              <dd>
                <Chip>Funcionário</Chip>
              </dd>
            </>
          )}
          <dt className="text-tinta-2">Telefone</dt>
          <dd className="tnum text-tinta-forte">{c.telefone}</dd>
          {c.documento && (
            <>
              <dt className="text-tinta-2">Documento</dt>
              <dd className="tnum text-tinta-forte">{c.documento}</dd>
            </>
          )}
          {c.codigoErp && (
            <>
              <dt className="text-tinta-2">ERP</dt>
              <dd className="text-tinta-forte">#{c.codigoErp}</dd>
            </>
          )}
          <dt className="text-tinta-2">Cadastrado</dt>
          <dd className="tnum text-tinta-forte">{formatarData(c.criadoEm)}</dd>
        </dl>
        {c.codigo && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-rotulo uppercase text-tinta-2">Código</span>
            <PlacaCodigo codigo={c.codigo.codigo} />
          </div>
        )}
      </div>

      {/* Posição no programa (E2 — tudo derivado pela API) */}
      <section>
        <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
          Posição no programa
        </h2>
        <div className="rounded-2 border border-linha bg-superficie p-4 text-corpo text-tinta">
          {cr.cicloReiniciou && cr.totalConvertidas >= 4 && cr.proximoBrinde ? (
            <p>
              Já ganhou os {4} brindes do ciclo. Recomeçou — próximo:{' '}
              <strong>{cr.proximoBrinde}</strong>.
            </p>
          ) : cr.proximoBrinde ? (
            <p>
              Nível {cr.nivelAtual} de 4 · próximo brinde: <strong>{cr.proximoBrinde}</strong> ·{' '}
              {cr.totalConvertidas} indicaç
              {cr.totalConvertidas === 1 ? 'ão convertida' : 'ões convertidas'} na vida
              {cr.cicloReiniciou ? ' · o ciclo já reiniciou' : ''}.
            </p>
          ) : (
            <p>
              {cr.totalConvertidas} indicaç
              {cr.totalConvertidas === 1 ? 'ão convertida' : 'ões convertidas'} na vida. O programa
              foi encerrado — indicações não geram mais brinde.
            </p>
          )}
        </div>
      </section>

      <BlocoVouchers clienteId={id} />
      <BlocoIndicacoesFeitas clienteId={id} primeiro={primeiro} />
      <BlocoIndicacaoRecebida clienteId={id} indicadoPor={c.indicadoPor} />
      <BlocoOrdens clienteId={id} />
    </div>
  );
}

// ── Vouchers ─────────────────────────────────────────────────────────────────
function BlocoVouchers({ clienteId }: { clienteId: string }): ReactNode {
  const q = useQuery({
    queryKey: ['cliente', clienteId, 'vouchers'],
    queryFn: () => listarVouchersDoCliente(clienteId),
  });
  const [entrega, setEntrega] = useState<VoucherCliente | null>(null);

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Brindes / vouchers
      </h2>
      {q.isLoading && <LinhasEsqueleto linhas={2} colunas={4} />}
      {q.isError && <p className="text-corpo text-tinta-2">—</p>}
      {q.data && q.data.length === 0 && (
        <p className="text-corpo text-tinta-2">Nenhum brinde ainda.</p>
      )}
      {q.data && q.data.length > 0 && (
        <table className="w-full border-collapse text-corpo">
          <thead>
            <tr className="border-b border-linha text-left font-display text-rotulo uppercase text-tinta-2">
              <th className="py-2 font-normal">Brinde</th>
              <th className="py-2 font-normal">Papel</th>
              <th className="py-2 font-normal">Código</th>
              <th className="py-2 font-normal">Status</th>
              <th className="py-2 font-normal" />
            </tr>
          </thead>
          <tbody>
            {q.data.map((v) => (
              <tr key={v.id} className="border-b border-linha">
                <td className="py-2.5 text-tinta-forte">{v.descricao}</td>
                <td className="py-2.5">
                  {v.papel === 'indicador' ? `Indicador nível ${v.nivel ?? '?'}` : 'Boas-vindas'}
                </td>
                <td className="py-2.5 font-codigo text-[0.8125rem]">{v.voucherCodigo}</td>
                <td className="py-2.5">
                  {v.status === 'disponivel' ? (
                    <Chip tom="acento">Disponível</Chip>
                  ) : (
                    <Chip tom="apagado">Entregue</Chip>
                  )}
                </td>
                <td className="py-2.5 text-right">
                  {v.status === 'disponivel' ? (
                    <Botao
                      variante="secundario"
                      className="h-8 px-3 text-[0.8125rem]"
                      onClick={() => setEntrega(v)}
                    >
                      Registrar entrega
                    </Botao>
                  ) : (
                    <span className="text-nota text-tinta-3">
                      {v.entregueEm ? formatarData(v.entregueEm) : ''}
                      {v.retiradoPor ? ` · ${v.retiradoPor}` : ''}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {entrega && (
        <DrawerEntrega
          aberto
          voucherId={entrega.id}
          brinde={entrega.descricao}
          aoFechar={() => setEntrega(null)}
        />
      )}
    </section>
  );
}

// ── Indicações feitas ────────────────────────────────────────────────────────
function BlocoIndicacoesFeitas({
  clienteId,
  primeiro,
}: {
  clienteId: string;
  primeiro: string;
}): ReactNode {
  const q = useQuery({
    queryKey: ['cliente', clienteId, 'indicacoes'],
    queryFn: () => obterIndicacoesDoCliente(clienteId),
  });

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Indicações feitas
      </h2>
      {q.isLoading && <LinhasEsqueleto linhas={2} colunas={4} />}
      {q.isError && <p className="text-corpo text-tinta-2">—</p>}
      {q.data && q.data.feitas.length === 0 && (
        <p className="text-corpo text-tinta-2">{primeiro} ainda não indicou ninguém.</p>
      )}
      {q.data && q.data.feitas.length > 0 && (
        <table className="w-full border-collapse text-corpo">
          <thead>
            <tr className="border-b border-linha text-left font-display text-rotulo uppercase text-tinta-2">
              <th className="py-2 font-normal">Indicado</th>
              <th className="py-2 font-normal">Data</th>
              <th className="py-2 font-normal">Resultado</th>
              <th className="py-2 font-normal">Motivo / brinde</th>
            </tr>
          </thead>
          <tbody>
            {q.data.feitas.map((i) => (
              <LinhaIndicacao key={i.id} i={i} campoPessoa={i.indicado?.nome ?? '—'} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function LinhaIndicacao({ i, campoPessoa }: { i: IndicacaoItem; campoPessoa: string }): ReactNode {
  const brinde = i.brindes.find((b) => b.papel === 'indicador') ?? i.brindes[0];
  return (
    <tr className="border-b border-linha">
      <td className="py-2.5 text-tinta-forte">{campoPessoa}</td>
      <td className="tnum py-2.5">{formatarData(i.convertidaEm ?? i.criadoEm)}</td>
      <td className="py-2.5">
        {i.status === 'convertida' ? (
          <Chip tom="sucesso">Convertida</Chip>
        ) : (
          <Chip tom="aviso">Rejeitada</Chip>
        )}
      </td>
      <td className="py-2.5 text-tinta-2">
        {i.status === 'rejeitada' && i.motivoRejeicao
          ? mensagemDeErro(i.motivoRejeicao)
          : (brinde?.descricao ?? '—')}
      </td>
    </tr>
  );
}

// ── Indicação recebida ───────────────────────────────────────────────────────
function BlocoIndicacaoRecebida({
  clienteId,
  indicadoPor,
}: {
  clienteId: string;
  indicadoPor: { id: string; nome: string } | null;
}): ReactNode {
  const q = useQuery({
    queryKey: ['cliente', clienteId, 'indicacoes'],
    queryFn: () => obterIndicacoesDoCliente(clienteId),
  });
  const recebida = q.data?.recebidas[0];

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Indicação recebida
      </h2>
      {!indicadoPor && !recebida ? (
        <p className="text-corpo text-tinta-2">Cadastro orgânico — não veio por indicação.</p>
      ) : (
        <p className="text-corpo text-tinta">
          Indicado por{' '}
          <Link
            to={indicadoPor ? `/clientes/${indicadoPor.id}` : '#'}
            className="text-acento-texto underline"
          >
            {indicadoPor?.nome ?? recebida?.indicador?.nome}
          </Link>
          {recebida?.ordemServico ? ` · OS ${recebida.ordemServico.numeroOs}` : ''}
          {recebida ? ` · ${formatarData(recebida.convertidaEm ?? recebida.criadoEm)}` : ''}
        </p>
      )}
    </section>
  );
}

// ── Ordens de serviço (para cancelar — §5) ───────────────────────────────────
function BlocoOrdens({ clienteId }: { clienteId: string }): ReactNode {
  const q = useQuery({
    queryKey: ['ordens', 'cliente', clienteId],
    queryFn: () => listarOrdensDoCliente(clienteId),
  });
  const [cancelar, setCancelar] = useState<OrdemServico | null>(null);

  return (
    <section>
      <h2 className="mb-2 font-display text-bloco-titulo font-bold uppercase text-tinta-forte">
        Ordens de serviço
      </h2>
      {q.isLoading && <LinhasEsqueleto linhas={2} colunas={3} />}
      {q.data && q.data.itens.length === 0 && (
        <p className="text-corpo text-tinta-2">Nenhuma OS registrada.</p>
      )}
      {q.data && q.data.itens.length > 0 && (
        <table className="w-full border-collapse text-corpo">
          <thead>
            <tr className="border-b border-linha text-left font-display text-rotulo uppercase text-tinta-2">
              <th className="py-2 font-normal">Número</th>
              <th className="py-2 font-normal">Serviço</th>
              <th className="py-2 font-normal">Data</th>
              <th className="py-2 font-normal" />
            </tr>
          </thead>
          <tbody>
            {q.data.itens.map((os) => (
              <tr key={os.id} className="border-b border-linha">
                <td className="tnum py-2.5 text-tinta-forte">{os.numeroOs}</td>
                <td className="py-2.5">{os.descricao}</td>
                <td className="tnum py-2.5">{formatarData(os.finalizadaEm ?? os.criadoEm)}</td>
                <td className="py-2.5 text-right">
                  {os.status === 'cancelada' ? (
                    <span className="text-nota text-tinta-3">
                      Cancelada em {os.canceladaEm ? formatarData(os.canceladaEm) : '—'}
                      {os.motivoCancelamento ? ` · ${os.motivoCancelamento}` : ''}
                    </span>
                  ) : (
                    <Botao
                      variante="secundario"
                      className="h-8 px-3 text-[0.8125rem]"
                      onClick={() => setCancelar(os)}
                    >
                      Cancelar OS
                    </Botao>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cancelar && (
        <DrawerCancelarOS
          aberto
          osId={cancelar.id}
          numeroOs={cancelar.numeroOs}
          aoFechar={() => setCancelar(null)}
        />
      )}
    </section>
  );
}
